import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  message,
  Modal,
  notification,
  Row,
  Select,
  Switch,
  Tag,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import styled from "styled-components";
import { cloneDeep } from "lodash";
import QuantityControl from "components/shared-components/QuantityControl";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

// Constants
import { ORDER_STATUS_CREATED, SOO_COOL_DELIVERY_LABELS } from "../../../../../constants/OrderConstant";
import {
  FIRESTORE_LEADS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { LEAD_STATUS_TRIAL_SENT } from "../../../../../constants/LeadConstant";

// Functions
import { getPickingDate } from "../../../../../functions/deliveryValidation";
import axios from "axios";
import urls from "../../../../../urls";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { useStockData } from "../../../../../data/hooks/firestore/useStock";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import dayjs from "dayjs";
import EditAddressModal from "./EditAddressModal";

const { Option } = Select;

const ConfirmDraftShipmentModal = ({ visible, onClose, order, products, leads }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { email } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [deliveryLabel, setDeliveryLabel] = useState("SCH");
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState(null);
  const [orderProducts, setOrderProducts] = useState([]);
  const [newProduct, setNewProduct] = useState("");
  const [productsExceedingStock, setProductsExceedingStock] = useState({});
  const [tempDeliveryDate, setTempDeliveryDate] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const { stockData, stockCarryOverData } = useStockData();
  const [selectedDeliveryDateForStock, setSelectedDeliveryDateForStock] = useState(null);
  const [editAddressModalVisible, setEditAddressModalVisible] = useState(false);
  const [tempAddress, setTempAddress] = useState(null);
  const [lead, setLead] = useState(null);
  const { getCountryDelivery, getCountryDeliveryLabels, getDeliveryLabelLeadDays } = useCountries();

  // Delivery date -> packing dateId (DDMMYYYY), using the lead time
  // configured for the lead's country + delivery label.
  const getShipmentPackingDateId = (deliveryDate) => {
    if (!deliveryDate) return null;
    const leadDays = getDeliveryLabelLeadDays(lead?.country || COMPANY_COUNTRY_CODE, deliveryLabel);
    return getPickingDate(deliveryDate, leadDays).format("DDMMYYYY");
  };

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  // Filter products: default only Chef's Farm, with switch for all products
  const productsToUse = useMemo(() => {
    if (showAllProducts) {
      return products;
    }
    return products?.filter((p) => p.company === CHEFS_FARM && p.productInSeason === true);
  }, [products, showAllProducts]);

  // Helpers for stock logic
  const isStockCarriedOver = (packingDateId) => stockCarryOverData[packingDateId] === true;

  const getStockInfo = (packingDateId, productId, source = stockData) => {
    return (
      source?.[packingDateId]?.[productId] || {
        currentStock: 0,
        projectedStock: 0,
        requestedStock: 0,
      }
    );
  };

  const getAvailableStock = (stockInfo, carriedOver) => {
    return carriedOver
      ? stockInfo.currentStock || 0
      : (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
  };

  // (Re)set state when modal opens/closes
  useEffect(() => {
    if (visible && order) {
      resetState();

      // Fetch lead data
      const fetchLead = async () => {
        try {
          const leadDoc = await getDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, order.leadId),
          );
          if (leadDoc.exists()) {
            setLead(leadDoc.data());
          }
        } catch (error) {
          console.error("Error fetching lead:", error);
          message.error(t("notifications.error_fetching_lead"));
        }
      };

      fetchLead();

      // Parse delivery time from order if available
      let timeSlotValue = null;
      if (order.deliveryTime && order.deliveryTime.includes("-")) {
        const [start, end] = order.deliveryTime.split("-");
        const timeSlot = {
          start: start.trim(),
          end: end.trim(),
          deliverySurcharge: order.deliveryTimeCharge || 0,
        };
        timeSlotValue = stringifyTimeSlot(timeSlot);
        setDeliveryTimeSlot(timeSlot);
      }

      form.setFieldsValue({
        deliveryDate: dayjs(order.farmorderDeliveryDate),
        deliveryLabel: order.deliveryLabel,
        deliveryTimeSlot: timeSlotValue,
      });
      setOrderProducts(order.userShoppingCart || []);
      setDeliveryLabel(order.deliveryLabel);
      setSelectedDeliveryDateForStock(dayjs(order.farmorderDeliveryDate));
    }
  }, [visible, order]);

  const resetState = () => {
    form.resetFields();
    setDeliveryLabel("SCH");
    setDeliveryTimeSlot(null);
    setOrderProducts([]);
    setNewProduct("");
    setProductsExceedingStock({});
    setTempDeliveryDate(null);
    setLoading(false);
    setShowAllProducts(false);
    setSelectedDeliveryDateForStock(null);
    setTempAddress(null);
    setLead(null);
  };

  const validateAndUpdateDate = async (date) => {
    if (!date || !orderProducts || orderProducts.length === 0) return;

    try {
      const packingDateId = getShipmentPackingDateId(date);
      const newStockData = stockData[packingDateId] || {};
      const stockCarriedOver = isStockCarriedOver(packingDateId);

      const unavailableProducts = [];
      const exceedingProducts = {};

      for (const product of orderProducts) {
        const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: newStockData });
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

        if (product.itemCount > availableStock) {
          unavailableProducts.push({
            name: product.productName,
            ordered: product.itemCount,
            available: availableStock,
          });
          exceedingProducts[product.productId] = true;
        }
      }

      if (unavailableProducts.length > 0) {
        Modal.confirm({
          title: t("notifications.stock_issues_for_selected_date"),
          content: (
            <div>
              <p>{t("notifications.insufficient_stock_for_date")}</p>
              <ul style={{ margin: "0 0 20px 20px", padding: 0 }}>
                {unavailableProducts.map((product, index) => (
                  <li key={index}>
                    {product.name}: {t("ordered")} {product.ordered}, {t("available")} {product.available}
                  </li>
                ))}
              </ul>
              <p>{t("proceed_anyway_negative_stock_warning")}</p>
            </div>
          ),
          zIndex: 1100,
          okText: t("proceed_anyway"),
          cancelText: t("cancel"),
          onOk: () => {
            form.setFieldsValue({ deliveryDate: date });
            setTempDeliveryDate(date);
            setProductsExceedingStock(exceedingProducts);
          },
          onCancel: () => {
            form.setFieldsValue({ deliveryDate: tempDeliveryDate });
          },
        });
      } else {
        form.setFieldsValue({ deliveryDate: date });
        setTempDeliveryDate(date);
        setProductsExceedingStock({});
      }
    } catch (error) {
      console.error("Error checking stock for new date:", error);
      message.error(t("notifications.failed_check_stock"));
    }
  };

  const productDropdownOptions = useMemo(() => {
    const deliveryDate = selectedDeliveryDateForStock || form.getFieldValue("deliveryDate");
    if (!deliveryDate) return [];

    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    return [...productsToUse]
      ?.filter((item) => !orderProducts.some((orderProduct) => orderProduct.productId === item.productId))
      ?.sort((a, b) => a.productName.localeCompare(b.productName))
      ?.map((product) => {
        const { productId, productName } = product;
        const stockInfo = getStockInfo(packingDateId, productId, stockData);
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);
        const isInStock = availableStock > 0;

        return {
          productId,
          productName,
          availableStock,
          isInStock,
        };
      });
  }, [productsToUse, orderProducts, selectedDeliveryDateForStock, stockData, stockCarryOverData, form]);

  const addProductToOrder = (product) => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    const newProduct = {
      externalId: product.exactId?.toString() || "",
      itemCount: 1,
      orderable: true,
      productCategory: product.category?.en || "",
      productId: product.id,
      productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
      productName: product.productName,
      productPrice: "0.00",
      productVat: 0,
      productBarcode: product.barcode || "",
    };

    setOrderProducts((prevProducts) => [...prevProducts, newProduct]);

    const availableStock = getAvailableStock(stockInfo, stockCarriedOver);
    if (availableStock < 1) {
      setProductsExceedingStock((prev) => ({
        ...prev,
        [product.id]: true,
      }));
    }
  };

  const increaseQuantity = (product) => {
    const index = orderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(orderProducts);
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    if (index !== -1) {
      newProducts[index].itemCount += 1;
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (newProducts[index].itemCount > availableStock) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      } else {
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setOrderProducts(newProducts);
  };

  const decreaseQuantity = (product) => {
    const index = orderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(orderProducts);

    if (index !== -1) {
      newProducts[index].itemCount -= 1;

      if (productsExceedingStock[product.productId]) {
        const deliveryDate = form.getFieldValue("deliveryDate");
        const packingDateId = getShipmentPackingDateId(deliveryDate);
        const stockCarriedOver = isStockCarriedOver(packingDateId);
        const stockInfo = getStockInfo(packingDateId, product.productId, stockData);
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

        if (newProducts[index].itemCount <= availableStock) {
          setProductsExceedingStock((prev) => {
            const newState = { ...prev };
            delete newState[product.productId];
            return newState;
          });
        }
      }

      if (newProducts[index].itemCount === 0) {
        newProducts.splice(index, 1);
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setOrderProducts(newProducts);
  };

  const validateStock = () => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const latestStockData = stockData[packingDateId] || {};
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    const stockErrors = [];
    for (const product of orderProducts) {
      const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: latestStockData });
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (product.itemCount > availableStock) {
        stockErrors.push({
          name: product.productName,
          ordered: product.itemCount,
          available: availableStock,
        });
      }
    }

    return stockErrors;
  };

  const showOperationSummary = (results) => {
    const successfulOps = [];
    const failedOps = [];

    Object.entries(results).forEach(([operation, result]) => {
      if (!result.attempted) return;

      if (result.success && !result.error) {
        successfulOps.push(getOperationDisplayName(operation));
      } else if (result.success && result.error) {
        successfulOps.push(getOperationDisplayName(operation));
      } else {
        failedOps.push({ operation: getOperationDisplayName(operation), error: result.error });
      }
    });

    const hasFailures = failedOps.length > 0;
    const title = hasFailures
      ? t("notifications.shipment_creation_partial")
      : t("notifications.shipment_creation_complete");

    const content = (
      <div>
        {successfulOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#52c41a" }}>{t("successful_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {successfulOps.map((op, index) => (
                <li key={index}>{op}</li>
              ))}
            </ul>
          </div>
        )}

        {failedOps.length > 0 && (
          <div>
            <strong style={{ color: "#ff4d4f" }}>{t("failed_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {failedOps.map(({ operation, error }, index) => (
                <li key={index}>
                  {operation}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    notification.destroy();
    notification[hasFailures ? "error" : "success"]({
      message: title,
      description: content,
      duration: 0,
      style: { width: "400px" },
    });
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      leadUpdate: t("lead_address_updated"),
      firestore: t("shipment_confirmed"),
      stock: t("stock_updated"),
      sooCool: t("notifications.soocool_delivery_created"),
    };
    return names[operation] || operation;
  };

  const handleSubmit = (bypassCheck = false) => {
    form.validateFields().then(async (values) => {
      try {
        if (orderProducts.length === 0) {
          message.error(t("notifications.add_products_first"));
          return;
        }

        setLoading(true);

        const operationResults = {
          leadUpdate: { attempted: false, success: false, error: null },
          firestore: { attempted: true, success: false, error: null },
          stock: { attempted: false, success: false, error: null },
          sooCool: { attempted: false, success: false, error: null },
        };

        // Validate stock unless bypassed
        if (!bypassCheck) {
          const stockErrors = validateStock();
          if (stockErrors.length) {
            Modal.confirm({
              title: t("notifications.stock_validation_failed"),
              content: (
                <div>
                  <p>{t("notifications.products_exceed_stock")}</p>
                  <ul>
                    {stockErrors.map((error, index) => (
                      <li key={index}>
                        {error.name}: {t("ordered")} {error.ordered}, {t("available")} {error.available}
                      </li>
                    ))}
                  </ul>
                  <p>{t("proceed_anyway_negative_stock_warning")}</p>
                </div>
              ),
              okText: t("proceed_anyway"),
              cancelText: t("cancel"),
              onOk: () => handleSubmit(true),
              onCancel: () => setLoading(false),
            });
            return;
          }
        }

        // UPDATE LEAD ADDRESS IF CHANGED
        if (tempAddress) {
          operationResults.leadUpdate = { attempted: true, success: false, error: null };
          try {
            const address =
              String(tempAddress.street || "").trim() +
              " " +
              String(tempAddress.houseNumber ?? "").trim() +
              (tempAddress.houseNumberAddition ? " " + String(tempAddress.houseNumberAddition).trim() : "") +
              ", " +
              String(tempAddress.city || "").trim() +
              ", " +
              String(tempAddress.zipCode || "").trim();

            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, order.leadId),
              {
                street: String(tempAddress.street || "").trim(),
                houseNumber: String(tempAddress.houseNumber ?? "").trim(),
                houseNumberAddition: tempAddress.houseNumberAddition
                  ? String(tempAddress.houseNumberAddition).trim()
                  : null,
                zipCode: String(tempAddress.zipCode || "").trim(),
                city: String(tempAddress.city || "").trim(),
                country: tempAddress.country,
                address: address,
                updatedAt: dayjs().unix(),
                updatedBy: email,
              },
            );
            operationResults.leadUpdate.success = true;
          } catch (leadError) {
            console.error("Error updating lead address:", leadError);
            operationResults.leadUpdate.error = leadError.message;
          }
        }

        // Get delivery time and charge from selected time slot
        let deliveryTime = "10:00-18:00";
        let deliveryTimeCharge = 0;
        let deliveryTimeSlotObject = null;

        const selectedTimeSlotValue = form.getFieldValue("deliveryTimeSlot");
        if (selectedTimeSlotValue) {
          try {
            const timeSlot = JSON.parse(selectedTimeSlotValue);
            deliveryTime = `${timeSlot.start}-${timeSlot.end}`;
            deliveryTimeCharge = timeSlot.deliverySurcharge || 0;
            deliveryTimeSlotObject = timeSlot;
          } catch (e) {
            console.error("Error parsing time slot:", e);
          }
        }

        const orderObj = {
          ...order,
          farmorderDeliveryDate: dayjs(values.deliveryDate).format("YYYY-MM-DD"),
          deliveryLabel: deliveryLabel,
          deliveryTime: deliveryTime,
          deliveryTimeCharge: deliveryTimeCharge,
          deliveryTimeSlot: deliveryTimeSlotObject,
          userShoppingCart: orderProducts,
          farmorderStatus: ORDER_STATUS_CREATED,
          farmorderDateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
          orderDateTime: dayjs().unix() * 1000,
          subTotalAmount: 0,
          subTotalWithoutDiscount: 0,
          invoiceAdditionalCosts: 0,
          shippingCost: "0",
          totalAmount: 0,
          tax: 0,
          updatedBy: `CMS - ${email}`,
        };

        // UPDATE FIRESTORE
        try {
          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, order.id),
            orderObj,
          );

          operationResults.firestore.success = true;

          // UPDATE LEAD STATUS TO TRIAL_SENT
          try {
            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, order.leadId),
              { status: LEAD_STATUS_TRIAL_SENT },
            );
          } catch (leadError) {
            console.error("Error updating lead status:", leadError);
          }

          // STOCK API
          operationResults.stock = { attempted: true, success: false, error: null };
          try {
            const processToken = await getFirebaseIDToken();
            const orderPayload = {
              ...orderObj,
              farmorderId: order.farmorderId,
              id: order.id,
            };

            const stockResponse = await axios.post(urls.stockApiUrl, orderPayload, {
              headers: { Authorization: processToken },
            });

            const stockData = stockResponse.data;
            if (stockData && typeof stockData.success === "boolean") {
              operationResults.stock.success = stockData.success;
              if (!stockData.success) {
                operationResults.stock.error = stockData.message || "Stock update returned an error";
              }
            } else {
              operationResults.stock.success = true;
            }
          } catch (stockError) {
            console.error("❌ Stock API error:", stockError);
            operationResults.stock.error = stockError.response?.data?.message || stockError.message;
          }

          // SOOCOOL API (if applicable)
          if (SOO_COOL_DELIVERY_LABELS.includes(deliveryLabel)) {
            operationResults.sooCool = { attempted: true, success: false, error: null };
            try {
              const processToken = await getFirebaseIDToken();
              const orderPayload = {
                ...orderObj,
                farmorderId: order.farmorderId,
                id: order.id,
              };

              await axios.post(urls.sooCoolOrder, orderPayload, {
                headers: { Authorization: processToken },
              });
              operationResults.sooCool.success = true;
            } catch (sooCoolError) {
              console.error("❌ SooCool API error:", sooCoolError);
              operationResults.sooCool.error = sooCoolError.response?.data?.message || sooCoolError.message;
            }
          }
        } catch (firestoreError) {
          console.error("Firestore error:", firestoreError);
          operationResults.firestore.error = firestoreError.message;
          setLoading(false);
          showOperationSummary(operationResults);
          return;
        }

        showOperationSummary(operationResults);
        resetState();
        onClose();
      } catch (error) {
        console.error("Error confirming shipment:", error);
        message.error(t("notifications.error_confirming_shipment") + ": " + error.message);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleAddressSave = (addressData) => {
    setTempAddress(addressData);
    setEditAddressModalVisible(false);
    message.success(t("notifications.address_updated_temp"));
  };

  const currentAddress = tempAddress || lead;

  return (
    <>
      <Modal
        title={t("confirm_draft_shipment")}
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="back" onClick={onClose}>
            {t("cancel")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={() => handleSubmit(false)}>
            {t("confirm_shipment")}
          </Button>,
        ]}
        width={700}
        centered
      >
        <Form layout="vertical" form={form}>
          {/* Address Display */}
          {lead && (
            <>
              <Descriptions
                title={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{t("delivery_address")}</span>
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => setEditAddressModalVisible(true)}
                      size="small"
                    >
                      {t("edit_address")}
                    </Button>
                  </div>
                }
                bordered
                size="small"
                column={1}
                style={{ marginBottom: 16 }}
              >
                {lead.status === "action_needed" && (
                  <Descriptions.Item label={t("lead_status.lead_status")}>
                    <Tag color={"orange"}>{t(`lead_status.${lead.status}`)}</Tag>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("chat_summary")}>{lead.shipmentInfo?.chatSummary}</Descriptions.Item>
                <Descriptions.Item label={t("street")}>
                  {currentAddress?.street} {currentAddress?.houseNumber}
                  {currentAddress?.houseNumberAddition ? ` ${currentAddress.houseNumberAddition}` : ""}
                </Descriptions.Item>
                <Descriptions.Item label={t("zip_code")}>{currentAddress?.zipCode}</Descriptions.Item>
                <Descriptions.Item label={t("city")}>{currentAddress?.city}</Descriptions.Item>
                <Descriptions.Item label={t("country")}>{currentAddress?.country}</Descriptions.Item>
              </Descriptions>
              <Divider style={{ marginTop: 0 }} />
            </>
          )}

          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="deliveryDate"
                label={t("delivery_date")}
                rules={[{ required: true, message: t("form.pick_value") }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  onChange={(date) => {
                    validateAndUpdateDate(date);
                    setSelectedDeliveryDateForStock(date);
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="deliveryLabel"
                label={t("delivery_label")}
                rules={[{ required: true, message: t("form.pick_value") }]}
              >
                <Select
                  onChange={(value) => {
                    setDeliveryLabel(value);
                    // Auto-set first time slot when label changes
                    const deliverySettings = getCountryDelivery(lead?.country || COMPANY_COUNTRY_CODE);
                    const labelConfig = deliverySettings?.deliveryLabels?.find((dl) => dl.deliveryLabel === value);
                    if (labelConfig?.deliveryTimeSlots?.length > 0) {
                      const firstSlot = labelConfig.deliveryTimeSlots[0];
                      form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(firstSlot));
                      setDeliveryTimeSlot(firstSlot);
                    }
                  }}
                  value={deliveryLabel}
                  disabled={!lead}
                >
                  {getCountryDeliveryLabels(lead?.country).map((d) => (
                    <Select.Option value={d.code} key={d.code}>
                      {d.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="deliveryTimeSlot"
                label={t("delivery_time_slot")}
                rules={[{ required: true, message: t("form.select_delivery_time_slot") }]}
              >
                <Select
                  placeholder={t("select_delivery_time_slot")}
                  disabled={!deliveryLabel || !lead}
                  onChange={(value) => {
                    try {
                      const slot = JSON.parse(value);
                      setDeliveryTimeSlot(slot);
                    } catch (e) {
                      console.error("Error parsing time slot:", e);
                    }
                  }}
                  optionLabelProp="label"
                >
                  {deliveryLabel &&
                    lead &&
                    (() => {
                      const deliverySettings = getCountryDelivery(lead.country || COMPANY_COUNTRY_CODE);
                      return deliverySettings?.deliveryLabels
                        ?.find((dl) => dl.deliveryLabel === deliveryLabel)
                        ?.deliveryTimeSlots?.map((slot, index) => {
                          const label = `${slot.start} - ${slot.end}${
                            slot.deliverySurcharge > 0 ? ` (+€${slot.deliverySurcharge})` : ""
                          }`;
                          return (
                            <Select.Option key={index} value={stringifyTimeSlot(slot)} label={label}>
                              {label}
                            </Select.Option>
                          );
                        });
                    })()}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <h3>{t("products")}</h3>
          <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch checked={showAllProducts} onChange={setShowAllProducts} size="small" />
            <span style={{ fontSize: "14px" }}>{t("show_all_products")}</span>
          </div>

          {orderProducts?.map((product) => {
            const { productId, productName, itemCount } = product;
            const isExceedingStock = productsExceedingStock[productId];

            return (
              <EditOrderCartItem
                key={productId}
                style={
                  isExceedingStock
                    ? {
                        borderLeft: "3px solid #ff4d4f",
                        paddingLeft: "8px",
                        backgroundColor: "rgba(255, 77, 79, 0.05)",
                      }
                    : {}
                }
              >
                <div style={{ display: "flex" }}>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const existingProductIndex = orderProducts.findIndex((item) => item.productId === productId);
                      if (existingProductIndex > -1) {
                        const newProducts = [...orderProducts];
                        newProducts.splice(existingProductIndex, 1);
                        setOrderProducts(newProducts);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    }}
                    style={{ marginRight: "5px" }}
                  />
                  <div>
                    <h4>{productName}</h4>
                    {isExceedingStock && (
                      <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {t("exceeds_available_stock")}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <QuantityControl
                    productId={productId}
                    currentQuantity={itemCount}
                    onIncrease={() => increaseQuantity(product)}
                    onDecrease={() => decreaseQuantity(product)}
                    onManualInput={(productId, value) => {
                      const existingProductIndex = orderProducts.findIndex((item) => item.productId === productId);
                      const deliveryDate = form.getFieldValue("deliveryDate");
                      const packingDateId = getShipmentPackingDateId(deliveryDate);
                      const stockCarriedOver = stockCarryOverData[packingDateId] === true;
                      const stockInfo = stockData[packingDateId]?.[productId] || {
                        currentStock: 0,
                        projectedStock: 0,
                        requestedStock: 0,
                      };

                      if (value === 0) {
                        if (existingProductIndex > -1) {
                          const newProducts = [...orderProducts];
                          newProducts.splice(existingProductIndex, 1);
                          setOrderProducts(newProducts);
                          setProductsExceedingStock((prev) => {
                            const newState = { ...prev };
                            delete newState[productId];
                            return newState;
                          });
                        }
                      } else if (existingProductIndex > -1) {
                        const newProducts = [...orderProducts];
                        newProducts[existingProductIndex].itemCount = value;
                        setOrderProducts(newProducts);

                        let availableStock;
                        if (stockCarriedOver) {
                          availableStock = stockInfo.currentStock;
                        } else {
                          availableStock = (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
                        }

                        if (value > availableStock) {
                          setProductsExceedingStock((prev) => ({
                            ...prev,
                            [productId]: true,
                          }));
                        } else {
                          setProductsExceedingStock((prev) => {
                            const newState = { ...prev };
                            delete newState[productId];
                            return newState;
                          });
                        }
                      }
                    }}
                  />
                </div>
              </EditOrderCartItem>
            );
          })}

          <NewProductsWrapper>
            <Select
              style={{ width: "100%" }}
              placeholder={t("add_products")}
              showSearch
              filterOption={(input, option) => {
                const productData = productDropdownOptions.find((p) => p.productId === option.value);
                return productData?.productName?.toLowerCase().includes(input.toLowerCase());
              }}
              onChange={(value) => {
                if (value) {
                  const product = productsToUse?.find((item) => item.productId === value);
                  const existsInCart = orderProducts.some((p) => p.productId === product.id);

                  if (existsInCart) {
                    increaseQuantity(product);
                  } else {
                    addProductToOrder(product);
                  }
                  setNewProduct(null);
                }
              }}
              value={newProduct}
            >
              {productDropdownOptions.map((productData) => (
                <Select.Option key={productData.productId} value={productData.productId}>
                  {productData.productName}
                  {productData.isInStock ? (
                    ` (${t("stock")}: ${productData.availableStock})`
                  ) : (
                    <span style={{ color: "#ff4d4f" }}>{` (${t("stock")}: ${productData.availableStock})`}</span>
                  )}
                </Select.Option>
              ))}
            </Select>
          </NewProductsWrapper>
        </Form>
      </Modal>

      <EditAddressModal
        visible={editAddressModalVisible}
        onClose={() => setEditAddressModalVisible(false)}
        onSave={handleAddressSave}
        initialAddress={currentAddress}
      />
    </>
  );
};

const NewProductsWrapper = styled.div`
  .ant-collapse-content-box {
    padding-inline: 0 !important;
  }
`;

const EditOrderCartItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

  h4 {
    margin: 0;
    font-size: 16px;
    line-height: 1.2;
  }

  p {
    margin: 0;
  }

  .quantity-chooser {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgb(232, 232, 232);
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1;
      padding-inline: 6px;
      min-width: 50px;
      text-align: center;
      color: #1a3353;
    }

    .anticon {
      font-size: 12px;
      padding: 8px 6px;
      cursor: pointer;
    }
  }
`;

export default ConfirmDraftShipmentModal;
